/* ============================================================================
 * utils.jsx — pure helpers (no React state)
 * ========================================================================= */

/* Human-readable labels for snake_case enum values */
export const LABELS = {
  customer: "Customer",
  reseller: "Reseller",
  supplier: "Supplier",
  restaurant: "Restaurant",
  partner: "Partner",

  cold_lead: "Cold Lead",
  warm_lead: "Warm Lead",
  new_customer: "New Customer",
  warm_customer: "Warm Customer",
  loyal_customer: "Loyal Customer",
  cold_customer: "Cold Customer",

  walk_in: "Walk-in",
  referral: "Referral",
  facebook: "Facebook",
  instagram: "Instagram",
  shopee: "Shopee",
  website: "Website",
  order_form: "Order Form",
  other: "Other",

  retail: "Retail",
  wholesale_small: "Wholesale Small",
  wholesale_large: "Wholesale Large",
  custom: "Custom",

  pending: "Pending",
  paid: "Paid",
  preparing: "Preparing",
  ready: "Ready for Pickup",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",

  cash: "Cash",
  gcash: "GCash",
  maya: "Maya",
  gotyme: "GoTyme",
  bpi: "BPI",
  bpi_atm: "BPI ATM",
  bpi_transfer: "BPI Transfer",
  bdo_debit: "BDO Debit",
  uniondigital: "UnionDigital",
  paymongo: "PayMongo",
  bank_transfer: "Bank Transfer",
  cod: "Cash on Delivery",
  lazada: "LazPay",
  "n/a": "N/A",

  pick_up: "Pick Up",
  delivery: "Delivery",
  meet_up: "Meet Up",

  business: "Business",
  personal: "Personal",

  flan: "Flan",
  ingredients: "Ingredients",
  packaging: "Packaging",
  active: "Active",

  admin: "Administrator",
  manager: "Manager",
  staff: "Staff",
  viewer: "Viewer",
};

export function humanize(value) {
  if (value === null || value === undefined || value === "") return "";
  if (LABELS[value]) return LABELS[value];
  return String(value)
    .split(/[_\s-]+/)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");
}

/* Phone normalization */
export function formatPhone(raw, cc) {
  cc = cc || (window.WYF_CONFIG && window.WYF_CONFIG.DEFAULT_COUNTRY_CODE) || "+63";
  if (!raw) return "";
  let s = String(raw).trim().replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+")) return s;
  if (cc === "+63") {
    if (s.startsWith("63")) return "+" + s;
    if (s.startsWith("0"))  return cc + s.slice(1);
    if (s.startsWith("9") && s.length === 10) return cc + s;
  }
  return cc + s;
}

export function validatePhone(raw, cc) {
  cc = cc || (window.WYF_CONFIG && window.WYF_CONFIG.DEFAULT_COUNTRY_CODE) || "+63";
  if (!raw) return false;
  const e164 = formatPhone(raw, cc);
  if (!/^\+\d{8,15}$/.test(e164)) return false;
  if (cc === "+63") {
    if (/^\+639\d{9}$/.test(e164)) return true;
    if (/^\+632\d{7,8}$/.test(e164)) return true;
    if (/^\+63\d{9,10}$/.test(e164)) return true;
    return false;
  }
  return true;
}

export function formatMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return "₱" + num.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatDate(d) {
  if (!d) return "—";
  // Date-only strings (YYYY-MM-DD) parsed as local date to avoid UTC midnight shift
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
  }
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric", timeZone: "Asia/Manila" });
}

export function formatDateTime(d) {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Manila" });
}

export function scheduledFor(order) {
  if (order.delivery_method === 'delivery' || order.delivery_method === 'pick_up') {
    return order.delivery_date ? { date: order.delivery_date, time: null } : null;
  }
  if (order.delivery_method === 'meet_up' && order.meetup_datetime) {
    return { date: order.meetup_datetime.slice(0, 10), time: order.meetup_datetime };
  }
  return null;
}

export function formatScheduled(order) {
  const s = scheduledFor(order);
  if (!s) return '—';
  const today    = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
  const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
  let dateLabel;
  if (s.date === today)         dateLabel = 'Today';
  else if (s.date === tomorrow) dateLabel = 'Tomorrow';
  else {
    const d = new Date(s.date + 'T00:00:00');
    dateLabel = d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  }
  if (s.time) {
    const t = new Date(s.time).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${dateLabel} · ${t}`;
  }
  return `${dateLabel} · ${order.delivery_method === 'delivery' ? 'Delivery' : 'Pick up'}`;
}

export function debounce(fn, ms) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), ms);
  };
}

/* Enum options */
export const ENUM_OPTIONS = {
  contact_type:        ["customer", "reseller", "supplier", "restaurant", "partner"],
  lifecycle_status:    ["cold_lead", "warm_lead", "new_customer", "warm_customer", "loyal_customer", "cold_customer"],
  contact_source:      ["walk_in", "referral", "facebook", "instagram", "shopee", "website", "order_form", "other"],
  price_tier:          ["retail", "reseller", "custom"],
  order_status:           ["pending", "paid", "preparing", "ready", "out_for_delivery", "delivered", "cancelled"],
  order_payment_method:   ["cash", "gcash", "maya", "gotyme", "bpi", "bpi_atm", "bpi_transfer", "bdo_debit", "uniondigital", "bank_transfer", "other"],
  expense_payment_method: ["cash", "bpi_atm", "bpi_transfer", "bdo_debit", "gcash", "maya", "gotyme", "uniondigital", "shopee", "lazada", "bank_transfer", "other", "n/a"],
  delivery_method:     ["pick_up", "delivery", "meet_up"],
  product_category:    ["Avocado Series", "Best Seller", "Japan Inspired", "Other", "Seasonal", "Solo Size"],
  service_type:        ["lalamove", "grab", "maxim", "joyride", "toktok", "foodpanda", "individual", "personal", "other"],
  errand_type:         ["supply_run", "delivery", "pickup", "other"],
  errand_status:       ["pending", "in_progress", "completed", "cancelled"],
  expense_classification: ["business", "personal"],
  user_role:           ["admin", "manager", "staff", "viewer"],
};

export const BUSINESS_CONTACT_TYPES = ["reseller", "supplier", "restaurant", "partner"];

export const UTENSILS_CATEGORY_ID = "e5e8b293-a081-4f82-9a85-0b5a8234e8cb";